import React, { useEffect, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as GoogleIcon } from '@pkg/assets/icons/v2/google.svg';
import { ReactComponent as MailIcon } from '@pkg/assets/icons/v2/mail.svg';
import { ReactComponent as AppleIcon } from '@pkg/assets/icons/v2/pay_apple.svg';
import { ReactComponent as DiscordIcon } from '@pkg/assets/icons/v2/social/discord.svg';
import { ReactComponent as TwitterIcon } from '@pkg/assets/icons/v2/social/twitter.svg';
import GemstoneUrl from '@pkg/assets/images/v2/emoji/gemstone.png';
import { FieldEmail, type FieldEmailProps, FieldPassword, type FieldPasswordProps } from '@pkg/components';
import { useLogin, useProfile } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';
import { TwoFactorAuthType } from '@pkg/types';

import { callProviderAuth } from '@app/client/resources/w3auth';
import { AuthProviders } from '@app/client/resources/w3auth/types';
import config from '@app/config';
import { YAT_PROFILE_PAGE } from '@app/routes/paths';
import { useAuthSidebar } from '@app/store/zustand';

import { Typography } from '../../DesignSystem';
import { DepButton } from '../../deprecated';
import {
    ButtonItem,
    ButtonList,
    Container,
    ContentLegend,
    EmojiImage,
    EmphasizedTypography,
    FormGroup,
    FormRow,
    FormRowReset,
    InternalLink,
    PrimaryCopySection,
    Section,
} from '../Web3Auth.style';
import { Web3AuthPanelRenderContext, Web3AuthSteps } from '../Web3Auth.types';
import { FormMessage, ProviderAction } from '../components';
import { useWeb3AuthContext, useWeb3RenderContext } from '../context';

const { authProviders, twoFAWallEnabled } = config;

// TODO: We should have a real source of truth
// for what our error responses are.
const KNOWN_ERRORS = {
    invalid: 'Invalid email or password.',
};

export function EntryPoint() {
    const [translate] = useTranslation(['auth', 'common', 'fantasy_wishlist']);
    const { tablet } = useMediaQuery();

    const { setAuthEmail, setCurrentStep } = useWeb3AuthContext();
    const { renderCtx } = useWeb3RenderContext();

    const navigate = useNavigate();

    const { loginWithPassword } = useLogin();
    const { redirectToProfile } = useProfile();
    const { postAuthRedirect } = useAuthSidebar();

    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [hasAuthError, setHasAuthError] = useState(false);

    const teamContext = renderCtx === Web3AuthPanelRenderContext.Teams;

    async function handleAuthProviderClick(provider: AuthProviders) {
        const link = await callProviderAuth(provider);

        if (link) {
            window.location.href = link;
        }
    }

    function handleEmailClick() {
        setCurrentStep(Web3AuthSteps.SignUpWithEmail);
    }

    function handleForgotClick() {
        setCurrentStep(Web3AuthSteps.ForgotPassword);
    }

    async function handleEmailLogin() {
        setLoading(true);
        setAuthEmail(email);

        try {
            const token = await loginWithPassword({ email, password });
            if (!token) throw new Error(KNOWN_ERRORS.invalid);

            const twoFAShouldPrompt = Boolean(token?.requires_2fa && token?.requires_2fa !== TwoFactorAuthType.Invalid);

            if (twoFAWallEnabled && twoFAShouldPrompt) {
                setCurrentStep(Web3AuthSteps.TwoFactorAuth);
            } else {
                if (postAuthRedirect) {
                    if (postAuthRedirect === YAT_PROFILE_PAGE) {
                        await redirectToProfile({ hardRedirect: true });
                    } else {
                        navigate(postAuthRedirect);
                    }
                } else {
                    redirectToProfile({ hardRedirect: true });
                }
            }
        } catch (error) {
            setHasAuthError(true);
        }

        setLoading(false);
    }

    const handleEmailChange: FieldEmailProps['onChange'] = (incomingValue) => {
        setEmail(`${incomingValue ?? ''}`);
    };

    const handlePasswordChange: FieldPasswordProps['onChange'] = (incomingValue) => {
        setPassword(`${incomingValue ?? ''}`);
    };

    useEffect(() => {
        if (hasAuthError) setHasAuthError(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    const errorMarkup = hasAuthError ? (
        <FormMessage key="FormMessage" message={KNOWN_ERRORS.invalid} autoScroll error />
    ) : null;

    return (
        <Container>
            <Section className="hide-on-tablet">
                <EmojiImage src={GemstoneUrl} />
            </Section>

            <PrimaryCopySection>
                <Typography type="headline" size={40}>
                    {teamContext
                        ? translate('w3a_panel_join_team_header', { ns: 'fantasy_wishlist' })
                        : translate('entry_point_heading')}
                </Typography>

                <Typography type="paragraph" size={18} faded={true}>
                    {translate('entry_point_subheading')}
                </Typography>
            </PrimaryCopySection>

            <Section>
                <ButtonList $condensed={!tablet}>
                    {authProviders.google && (
                        <ButtonItem>
                            <ProviderAction
                                large={tablet}
                                icon={<GoogleIcon />}
                                label={tablet ? translate('common:google') : undefined}
                                onAsyncAction={() => handleAuthProviderClick(AuthProviders.Google)}
                            />
                        </ButtonItem>
                    )}

                    {authProviders.twitter && (
                        <ButtonItem>
                            <ProviderAction
                                large={tablet}
                                icon={<TwitterIcon style={{ fill: 'hsla(204, 88%, 53%, 1)' }} />}
                                label={tablet ? translate('common:twitter') : undefined}
                                onAsyncAction={() => handleAuthProviderClick(AuthProviders.Twitter)}
                            />
                        </ButtonItem>
                    )}

                    {authProviders.apple && (
                        <ButtonItem>
                            <ProviderAction
                                large={tablet}
                                icon={<AppleIcon />}
                                label={tablet ? translate('common:apple') : undefined}
                                onAsyncAction={() => handleAuthProviderClick(AuthProviders.Apple)}
                            />
                        </ButtonItem>
                    )}

                    {authProviders.discord && (
                        <ButtonItem>
                            <ProviderAction
                                large={tablet}
                                icon={<DiscordIcon style={{ fill: 'hsla(235, 86%, 65%, 1)' }} />}
                                label={tablet ? translate('common:discord') : undefined}
                                onAsyncAction={() => handleAuthProviderClick(AuthProviders.Discord)}
                            />
                        </ButtonItem>
                    )}

                    <ButtonItem>
                        <ProviderAction
                            large={tablet}
                            icon={<MailIcon />}
                            label={tablet ? translate('common:email') : undefined}
                            onAction={handleEmailClick}
                        />
                    </ButtonItem>
                </ButtonList>

                <ContentLegend>
                    <Typography type="paragraph" size={16} color="rgba(255,255,255,0.5)" weight="700">
                        <Trans ns="auth" i18nKey="or_log_in_with_email">
                            <EmphasizedTypography>0</EmphasizedTypography>
                        </Trans>
                    </Typography>
                </ContentLegend>

                <FormGroup onSubmit={handleEmailLogin}>
                    <FormRow>
                        <FieldEmail
                            label={translate('common:email')}
                            value={email}
                            error={hasAuthError}
                            onChange={handleEmailChange}
                        />
                    </FormRow>

                    <FormRow>
                        <FieldPassword
                            value={password}
                            label={translate('common:password')}
                            error={hasAuthError}
                            onChange={handlePasswordChange}
                        />

                        <DepButton
                            type="submit"
                            variant="glow"
                            disabled={!(email && password)}
                            loading={loading}
                            onClick={handleEmailLogin}
                        >
                            {translate('email_login_submit')}
                        </DepButton>
                    </FormRow>

                    <FormRow>
                        <FormRowReset>
                            <AnimatePresence>{errorMarkup}</AnimatePresence>

                            <InternalLink href="#" onClick={handleForgotClick}>
                                {translate('forgot_password_action')}
                            </InternalLink>
                        </FormRowReset>
                    </FormRow>
                </FormGroup>
            </Section>
        </Container>
    );
}
